import React, { useState } from 'react';
import api from '../utils/api';
import './ExportData.css';

const ExportData = () => {
  const [selectedExport, setSelectedExport] = useState('teachers');
  const [selectedFormat, setSelectedFormat] = useState('json');
  const [isExporting, setIsExporting] = useState(false);
  const [message, setMessage] = useState(null);

  const exportOptions = [
    { value: 'teachers', label: 'All Teachers' },
    { value: 'admin-staff', label: 'All Admin Staff' },
    { value: 'faculty-staff', label: 'All Faculty Staff' },
    { value: 'service-staff', label: 'All Service Staff' },
    { value: 'faculties', label: 'All Faculties' },
    { value: 'departments', label: 'All Departments' },
    { value: 'courses', label: 'All Courses' },
    { value: 'admin-units', label: 'All Admin Units' },
    { value: 'admin-subunits', label: 'All Admin Subunits' },
    { value: 'services', label: 'All Services' },
    { value: 'service-subunits', label: 'All Service Subunits' },
    { value: 'all-people', label: 'All People (Combined)', featured: true },
    { value: 'complete-faculty-data', label: 'Complete Faculty Data', featured: true },
    { value: 'complete-admin-data', label: 'Complete Admin Data', featured: true },
    { value: 'complete-service-data', label: 'Complete Service Data', featured: true },
    { value: 'all-data', label: 'Complete Database Export', featured: true }
  ];

  const handleExport = async () => {
    setIsExporting(true);
    setMessage(null);

    try {
      const response = await api.get(`/export/${selectedExport}`, {
        params: { format: selectedFormat },
        responseType: selectedFormat === 'csv' ? 'blob' : 'json'
      });

      if (selectedFormat === 'csv') {
        // Handle CSV download
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${selectedExport}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setMessage({
          type: 'success',
          text: `CSV file downloaded successfully! (${selectedExport})`
        });
      } else {
        // Handle JSON download
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${selectedExport}_${new Date().toISOString().split('T')[0]}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setMessage({
          type: 'success',
          text: `JSON file downloaded successfully! (${response.data.count || response.data.data?.summary?.grandTotal || 'Multiple'} records)`
        });
      }
    } catch (error) {
      console.error('Export error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to export data. Please try again.'
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="export-data-container">
      <div className="export-header">
        <h2>Export Data</h2>
        <p>Export your data in JSON or CSV format with full UTF-8 support for Bengali text</p>
      </div>

      <div className="export-content">
        <div className="export-options-section">
          <h3>Select Data to Export</h3>
          <div className="export-options-grid">
            {exportOptions.map(option => (
              <div
                key={option.value}
                className={`export-option ${selectedExport === option.value ? 'selected' : ''} ${option.featured ? 'featured' : ''}`}
                onClick={() => setSelectedExport(option.value)}
              >
                <span className="option-label">{option.label}</span>
                {option.featured && <span className="featured-badge">✓</span>}
              </div>
            ))}
          </div>
        </div>

        <div className="export-format-section">
          <h3>Select Export Format</h3>
          <div className="format-options">
            <label className={`format-option ${selectedFormat === 'json' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="format"
                value="json"
                checked={selectedFormat === 'json'}
                onChange={(e) => setSelectedFormat(e.target.value)}
              />
              <span className="format-label">JSON</span>
              <span className="format-description">Structured data with full nesting</span>
            </label>

            <label className={`format-option ${selectedFormat === 'csv' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="format"
                value="csv"
                checked={selectedFormat === 'csv'}
                onChange={(e) => setSelectedFormat(e.target.value)}
              />
              <span className="format-label">CSV</span>
              <span className="format-description">Excel-compatible with UTF-8 BOM</span>
            </label>
          </div>
        </div>

        {message && (
          <div className={`export-message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="export-actions">
          <button
            className="export-button"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <span className="loading-spinner"></span>
                Exporting...
              </>
            ) : (
              <>
                Export {selectedFormat.toUpperCase()}
              </>
            )}
          </button>
        </div>

        <div className="export-info">
          <h4>Export Information</h4>
          <ul>
            <li><strong>JSON Format:</strong> Complete data with nested objects and arrays, ideal for importing into other systems</li>
            <li><strong>CSV Format:</strong> Flattened data with UTF-8 BOM for Excel compatibility, Bengali text fully supported</li>
            <li><strong>Data Population:</strong> All MongoDB ObjectID references are automatically populated with actual data</li>
            <li><strong>Featured Options:</strong> Combo exports combine multiple related collections for comprehensive data export</li>
            <li><strong>Security:</strong> All exports are protected and require authentication</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ExportData;
